// client/src/coms/personalisation/settingsControls.js
// Small accessible controls shared by the personalisation settings (radio groups, switches, sliders).
import React, { useId } from 'react';

export function Section({ title, description, children, actions, id }) {
  return (
    <section className="vx-set-section" aria-labelledby={id ? `${id}-title` : undefined} id={id}>
      <div className="vx-set-section-head">
        <div>
          <h3 className="vx-set-section-title" id={id ? `${id}-title` : undefined}>
            {title}
          </h3>
          {description ? <p className="vx-set-section-sub">{description}</p> : null}
        </div>
        {actions ? <div className="vx-set-section-actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

const SCOPE_LABELS = {
  account: ['Account', 'Saved to your account: follows you to every device you sign in on.'],
  device: ['This device', 'Saved in this browser or app only.'],
  server: ['This server', 'Applies only in the selected server.'],
};

// Where a setting is saved, shown next to its label.
export function ScopeBadge({ scope }) {
  const s = SCOPE_LABELS[scope];
  if (!s) return null;
  return (
    <span className={`vx-scope is-${scope}`} title={s[1]}>
      {s[0]}
    </span>
  );
}

// settingId: anchor used by Settings search (id="vx-setting-<settingId>").
// onReset: shows a "Reset" button while the value differs from its default (isDefault false).
export function Row({ label, hint, children, badge, settingId, scope, onReset, isDefault, resetLabel = 'Reset' }) {
  const id = useId();
  return (
    <div className="vx-set-row" role="group" aria-labelledby={id} id={settingId ? `vx-setting-${settingId}` : undefined} data-setting={settingId || undefined}>
      <div className="vx-set-row-label">
        <span id={id}>{label}</span>
        {badge}
        {scope ? <ScopeBadge scope={scope} /> : null}
        {hint ? <span className="vx-set-hint">{hint}</span> : null}
      </div>
      <div className="vx-set-row-control">
        {children}
        {onReset && isDefault === false ? (
          <button type="button" className="vx-link-btn vx-set-reset" onClick={onReset} aria-label={`${resetLabel}: ${typeof label === 'string' ? label : 'setting'}`}>
            {resetLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// options: [{ value, label, hint? }]
export function Segmented({ label, value, options, onChange, testId, disabled }) {
  return (
    <div className="vx-seg" role="radiogroup" aria-label={label} data-testid={testId}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={`vx-seg-btn${value === o.value ? ' is-on' : ''}`}
          onClick={() => onChange(o.value)}
          title={o.hint || undefined}
          disabled={disabled}
          data-value={String(o.value)}
          onKeyDown={(e) => {
            if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
            e.preventDefault();
            const i = options.findIndex((x) => x.value === value);
            const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
            const next = options[(i + d + options.length) % options.length];
            onChange(next.value);
            const sib = e.currentTarget.parentElement.querySelector(`[data-value="${String(next.value)}"]`);
            if (sib) sib.focus();
          }}
          tabIndex={value === o.value || (value == null && o === options[0]) ? 0 : -1}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Switch({ label, checked, onChange, testId }) {
  return (
    <button type="button" role="switch" aria-checked={!!checked} aria-label={label} className={`vx-switch${checked ? ' is-on' : ''}`} onClick={() => onChange(!checked)} data-testid={testId}>
      <span className="vx-switch-knob" aria-hidden="true" />
    </button>
  );
}

export function Slider({ label, value, min, max, step, onChange, format, testId }) {
  const id = useId();
  return (
    <div className="vx-slider">
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        aria-valuetext={format ? format(value) : String(value)}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
      />
      <output htmlFor={id} className="vx-slider-value">
        {format ? format(value) : value}
      </output>
    </div>
  );
}

export function InheritBadge({ inherited }) {
  return <span className={`vx-inherit${inherited ? '' : ' is-custom'}`}>{inherited ? 'Inherited' : 'This server'}</span>;
}
